import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LogOut, LayoutDashboard, Inbox, Download } from "lucide-react";
import { authApi } from "./api";
import { BRAND } from "../brand";

export default function AdminLayout() {
  const navigate = useNavigate();
  const logout = () => {
    authApi.logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/[0.06] bg-[#070707]/80 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-2.5 px-5 py-5" data-testid="admin-sidebar-logo">
          <img src={BRAND.logo} alt="Daos Ads" className="w-9 h-9 rounded-full ring-1 ring-white/10" />
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-[15px]">Daos Ads</div>
            <div className="text-[10px] text-zinc-500 tracking-[0.18em] uppercase">CRM Admin</div>
          </div>
        </Link>

        <nav className="px-3 py-2 flex-1 space-y-1">
          <SidebarLink to="/admin" end icon={LayoutDashboard} label="Dashboard" testid="admin-nav-dashboard" />
          <SidebarLink to="/admin/leads" icon={Inbox} label="Leads" testid="admin-nav-leads" />
        </nav>

        <div className="p-3 border-t border-white/[0.06] space-y-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export.csv`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-zinc-400 text-sm hover:text-white hover:bg-white/[0.04] transition"
            onClick={(e) => {
              const t = localStorage.getItem("daos_admin_token");
              if (!t) { e.preventDefault(); return; }
              // Browser cannot send Authorization on direct download — fallback covered by per-page button
            }}
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-zinc-400 text-sm hover:text-white hover:bg-white/[0.04] transition"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-white/[0.06] bg-[#070707]/80 backdrop-blur-xl sticky top-0 z-30">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logo} alt="Daos Ads" className="w-8 h-8 rounded-full ring-1 ring-white/10" />
            <div>
              <div className="font-display font-bold text-white text-sm">Daos Ads</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.18em] uppercase">CRM</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <NavLink to="/admin" end className={({ isActive }) => `text-xs px-3 py-1.5 rounded-full ${isActive ? "bg-white/[0.06] text-white" : "text-zinc-400"}`}>Dashboard</NavLink>
            <NavLink to="/admin/leads" className={({ isActive }) => `text-xs px-3 py-1.5 rounded-full ${isActive ? "bg-white/[0.06] text-white" : "text-zinc-400"}`}>Leads</NavLink>
            <button onClick={logout} className="text-xs text-zinc-400" data-testid="admin-logout-mobile">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="flex-1">
          <Outlet />
        </div>
      </main>

      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0a0a0a", border: "1px solid rgba(255,138,0,0.25)", color: "#fff" },
        }}
      />
    </div>
  );
}

function SidebarLink({ to, end, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-display transition ${
          isActive
            ? "bg-white/[0.06] text-white border border-white/[0.08]"
            : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
        }`
      }
    >
      <Icon className="w-4 h-4" />
      {label}
    </NavLink>
  );
}
